import { useEffect, useRef } from "react";

/** Auto-save a draft object to localStorage every `delay` ms (default 3s).
 *  Returns: { hasDraft, restore, clear, savedAt }
 *  Usage:
 *      const { hasDraft, restore, clear } = useDraft("ask", form, !!form.title || !!form.body);
 *      // on mount: if (hasDraft) prompt → setForm(restore())
 *      // on submit-success: clear()
 */
import { useState } from "react";

const PREFIX = "hc-draft-v1:";

export function useDraft(key, value, shouldSave, delay = 3000) {
    const storageKey = `${PREFIX}${key}`;
    const [savedAt, setSavedAt] = useState(null);
    const [hasDraft, setHasDraft] = useState(false);
    const timer = useRef(null);

    // Initial check — does a draft exist?
    useEffect(() => {
        try {
            const raw = window.localStorage.getItem(storageKey);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed && parsed.savedAt) {
                    setHasDraft(true);
                    setSavedAt(parsed.savedAt);
                }
            }
        } catch { /* noop */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storageKey]);

    // Debounced save
    useEffect(() => {
        if (!shouldSave) return;
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            try {
                const payload = { value, savedAt: new Date().toISOString() };
                window.localStorage.setItem(storageKey, JSON.stringify(payload));
                setSavedAt(payload.savedAt);
                setHasDraft(true);
            } catch { /* quota or disabled — silently ignore */ }
        }, delay);
        return () => { if (timer.current) clearTimeout(timer.current); };
    }, [value, shouldSave, delay, storageKey]);

    const restore = () => {
        try {
            const raw = window.localStorage.getItem(storageKey);
            if (!raw) return null;
            return JSON.parse(raw).value;
        } catch { return null; }
    };

    const clear = () => {
        try { window.localStorage.removeItem(storageKey); } catch { /* noop */ }
        setHasDraft(false);
        setSavedAt(null);
    };

    return { hasDraft, restore, clear, savedAt };
}

/** Simpler variant — state that persists itself. Returns [value, setValue, clearDraft]. */
export function useDraftState(key, initial) {
    const storageKey = `${PREFIX}state:${key}`;
    const [init] = useState(() => {
        try {
            const s = window.localStorage.getItem(storageKey);
            if (s) {
                const merged = { ...initial, ...JSON.parse(s) };
                return { val: merged, restored: JSON.stringify(merged) !== JSON.stringify(initial) };
            }
        } catch { /* noop */ }
        return { val: initial, restored: false };
    });
    const [val, setVal] = useState(init.val);
    useEffect(() => {
        const t = setTimeout(() => {
            try { window.localStorage.setItem(storageKey, JSON.stringify(val)); } catch { /* noop */ }
        }, 400);
        return () => clearTimeout(t);
    }, [storageKey, val]);
    const clear = () => { try { window.localStorage.removeItem(storageKey); } catch { /* noop */ } };
    return [val, setVal, clear, init.restored];
}
